import { Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, UrlTree } from '@angular/router';
import { filter, firstValueFrom, race, take, timer } from 'rxjs';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AppStateService {
  private router = inject(Router);
  private userService = inject(UserService);

  applyDarkMode(enabled: boolean): void {
    if (enabled) {
      document.documentElement.classList.add('ion-palette-dark');
    } else {
      document.documentElement.classList.remove('ion-palette-dark');
    }
  }

  createUrlTree(path: string, queryParams?: Record<string, unknown>): UrlTree {
    return this.router.createUrlTree([path], queryParams ? { queryParams } : undefined);
  }

  /**
   * On a cold start, push notification handling can fire while the router's initial
   * navigation is still resolving guards. Navigating in that window cancels the initial
   * navigation and can leave the root outlet with nothing ever activated (blank white screen).
   */
  private async waitForInitialNavigation(): Promise<void> {
    if (this.router.navigated) {
      return;
    }

    await firstValueFrom(
      race(
        this.router.events.pipe(
          filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
          take(1)
        ),
        timer(5000)
      )
    );
  }

  async navigateTo(path: string): Promise<void> {
    await this.waitForInitialNavigation();
    await this.router.navigate([path]);
  }

  async navigateToNotificationPage(page: string, currentPath: string): Promise<void> {
    await this.waitForInitialNavigation();

    if (currentPath.startsWith('/client') && page.startsWith('/worker')) {
      await this.userService.storeDashboard('worker');
    }

    if (currentPath.startsWith('/worker') && page.startsWith('/client')) {
      await this.userService.storeDashboard('client');
    }

    await this.router.navigate([page]);
  }
}
